import { supabase } from './supabase';

const KPI_CACHE_TTL_MS = 10 * 60 * 1000;
const KPI_CACHE_KEY = 'admin.analytics.globalKpis.v1';
const CLIENT_KPI_CACHE_PREFIX = 'admin.analytics.clientKpis.';
const REGION_KPI_CACHE_PREFIX = 'admin.analytics.regionKpis.';
const KPI_CACHE_SUFFIX = '.v1';
const CLIENT_KPI_CACHE_SUFFIX = KPI_CACHE_SUFFIX;
const IN_CHUNK_SIZE = 200;
const PAGE_SIZE = 1000;

function clientKpiCacheKey(clientId) {
  return `${CLIENT_KPI_CACHE_PREFIX}${clientId}${CLIENT_KPI_CACHE_SUFFIX}`;
}

function regionKpiCacheKey(clientId, regionId) {
  return `${REGION_KPI_CACHE_PREFIX}${clientId}.${regionId}${KPI_CACHE_SUFFIX}`;
}

export function readAdminGlobalKpisCache() {
  try {
    const raw = localStorage.getItem(KPI_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if ((Date.now() - (parsed?.savedAt || 0)) >= KPI_CACHE_TTL_MS) return null;
    return parsed.data || null;
  } catch {
    return null;
  }
}

export function writeAdminGlobalKpisCache(data) {
  try {
    localStorage.setItem(KPI_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data }));
  } catch {}
}

export async function getAdminGlobalKpis() {
  const nowIso = new Date().toISOString();
  const [
    clientsRes,
    usersRes,
    checkpointsRes,
    bootcampsRes,
    reflectionsRes,
  ] = await Promise.all([
    supabase.from('clients').select('*', { count: 'exact', head: true }),
    supabase.from('users').select('*', { count: 'exact', head: true }),
    supabase.from('lesson_completions').select('*', { count: 'exact', head: true }),
    supabase.from('bootcamps').select('*', { count: 'exact', head: true }).lt('end_date', nowIso),
    supabase.from('user_reflections').select('*', { count: 'exact', head: true }),
  ]);

  if (clientsRes.error) throw clientsRes.error;
  if (usersRes.error) throw usersRes.error;
  if (checkpointsRes.error) throw checkpointsRes.error;
  if (bootcampsRes.error) throw bootcampsRes.error;
  if (reflectionsRes.error) throw reflectionsRes.error;

  return {
    clients: clientsRes.count || 0,
    users: usersRes.count || 0,
    checkpointsCompleted: checkpointsRes.count || 0,
    bootcampsCompleted: bootcampsRes.count || 0,
    reflectionsWritten: reflectionsRes.count || 0,
  };
}

export function readAdminClientKpisCache(clientId) {
  try {
    const raw = localStorage.getItem(clientKpiCacheKey(clientId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if ((Date.now() - (parsed?.savedAt || 0)) >= KPI_CACHE_TTL_MS) return null;
    return parsed.data || null;
  } catch {
    return null;
  }
}

export function writeAdminClientKpisCache(clientId, data) {
  try {
    localStorage.setItem(clientKpiCacheKey(clientId), JSON.stringify({ savedAt: Date.now(), data }));
  } catch {}
}

export function readAdminRegionKpisCache(clientId, regionId) {
  try {
    const raw = localStorage.getItem(regionKpiCacheKey(clientId, regionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if ((Date.now() - (parsed?.savedAt || 0)) >= KPI_CACHE_TTL_MS) return null;
    return parsed.data || null;
  } catch {
    return null;
  }
}

export function writeAdminRegionKpisCache(clientId, regionId, data) {
  try {
    localStorage.setItem(regionKpiCacheKey(clientId, regionId), JSON.stringify({ savedAt: Date.now(), data }));
  } catch {}
}

async function countInChunks(table, column, ids, extraQuery) {
  let total = 0;
  for (let i = 0; i < ids.length; i += IN_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + IN_CHUNK_SIZE);
    let query = supabase.from(table).select('*', { count: 'exact', head: true }).in(column, chunk);
    if (extraQuery) query = extraQuery(query);
    const { count, error } = await query;
    if (error) throw error;
    total += count || 0;
  }
  return total;
}

export async function fetchLessonCompletionsForBootcamps(bootcampIds) {
  const ids = (bootcampIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  if (ids.length === 0) return [];

  const rows = [];
  for (let i = 0; i < ids.length; i += IN_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + IN_CHUNK_SIZE);
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from('lesson_completions')
        .select('user_id, lesson_id, bootcamp_id')
        .in('bootcamp_id', chunk)
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw error;
      if (data?.length) rows.push(...data);
      if (!data || data.length < PAGE_SIZE) break;
      from += PAGE_SIZE;
    }
  }
  return rows;
}

async function fetchDistinctCompletionUserIds(bootcampIds) {
  const userIds = new Set();
  for (let i = 0; i < bootcampIds.length; i += IN_CHUNK_SIZE) {
    const chunk = bootcampIds.slice(i, i + IN_CHUNK_SIZE);
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from('lesson_completions')
        .select('user_id')
        .in('bootcamp_id', chunk)
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw error;
      (data || []).forEach((row) => userIds.add(row.user_id));
      if (!data || data.length < PAGE_SIZE) break;
      from += PAGE_SIZE;
    }
  }
  return userIds;
}

export async function getAdminClientKpis(clientId) {
  const cid = String(clientId);

  const { data: regionsData, error: regionsError } = await supabase
    .from('regions')
    .select('id')
    .eq('client_id', cid)
    .eq('active', true);
  if (regionsError) throw regionsError;

  const regionIds = (regionsData || []).map((r) => r.id);
  let teamIds = [];
  if (regionIds.length > 0) {
    const { data: teamsData, error: teamsError } = await supabase
      .from('teams')
      .select('id')
      .in('region_id', regionIds)
      .eq('active', true);
    if (teamsError) throw teamsError;
    teamIds = (teamsData || []).map((t) => t.id);
  }

  const userIds = new Set();
  for (let i = 0; i < teamIds.length; i += IN_CHUNK_SIZE) {
    const chunk = teamIds.slice(i, i + IN_CHUNK_SIZE);
    const { data: teamUsersData, error: teamUsersError } = await supabase
      .from('team_users')
      .select('user_id')
      .in('team_id', chunk);
    if (teamUsersError) throw teamUsersError;
    (teamUsersData || []).forEach((row) => userIds.add(row.user_id));
  }

  let bootcamps = [];
  for (let i = 0; i < teamIds.length; i += IN_CHUNK_SIZE) {
    const chunk = teamIds.slice(i, i + IN_CHUNK_SIZE);
    const { data: bootcampsData, error: bootcampsError } = await supabase
      .from('bootcamps')
      .select('id, start_date, end_date')
      .in('team_id', chunk);
    if (bootcampsError) throw bootcampsError;
    bootcamps = bootcamps.concat(bootcampsData || []);
  }

  const { data: serverTimeData } = await supabase.rpc('get_server_time');
  const nowMs = new Date(serverTimeData || Date.now()).getTime();

  let bootcampsScheduled = 0;
  let bootcampsActive = 0;
  let bootcampsCompleted = 0;
  bootcamps.forEach((bc) => {
    const startTs = new Date(bc.start_date).getTime();
    const endTs = new Date(bc.end_date).getTime();
    if (startTs > nowMs) bootcampsScheduled += 1;
    else if (endTs <= nowMs) bootcampsCompleted += 1;
    else bootcampsActive += 1;
  });

  const bootcampIds = bootcamps.map((b) => b.id);
  let checkpointsCompleted = 0;
  let reflectionsWritten = 0;
  let activeUsers = 0;

  if (bootcampIds.length > 0) {
    const [checkpoints, reflections, completionUserIds] = await Promise.all([
      countInChunks('lesson_completions', 'bootcamp_id', bootcampIds),
      countInChunks('user_reflections', 'bootcamp_id', bootcampIds),
      fetchDistinctCompletionUserIds(bootcampIds),
    ]);
    checkpointsCompleted = checkpoints;
    reflectionsWritten = reflections;
    activeUsers = completionUserIds.size;
  }

  return {
    users: userIds.size,
    activeUsers,
    regions: regionIds.length,
    teams: teamIds.length,
    checkpointsCompleted,
    bootcamps: bootcamps.length,
    bootcampsScheduled,
    bootcampsActive,
    bootcampsCompleted,
    reflectionsWritten,
  };
}

export async function getAdminRegionKpis(clientId, regionId) {
  const cid = String(clientId);
  const rid = String(regionId);

  const { data: teamsData, error: teamsError } = await supabase
    .from('teams')
    .select('id')
    .eq('client_id', cid)
    .eq('region_id', rid)
    .eq('active', true);
  if (teamsError) throw teamsError;

  const teamIds = (teamsData || []).map((t) => t.id);

  const userIds = new Set();
  for (let i = 0; i < teamIds.length; i += IN_CHUNK_SIZE) {
    const chunk = teamIds.slice(i, i + IN_CHUNK_SIZE);
    const { data: teamUsersData, error: teamUsersError } = await supabase
      .from('team_users')
      .select('user_id')
      .in('team_id', chunk);
    if (teamUsersError) throw teamUsersError;
    (teamUsersData || []).forEach((row) => userIds.add(row.user_id));
  }

  let bootcamps = [];
  for (let i = 0; i < teamIds.length; i += IN_CHUNK_SIZE) {
    const chunk = teamIds.slice(i, i + IN_CHUNK_SIZE);
    const { data: bootcampsData, error: bootcampsError } = await supabase
      .from('bootcamps')
      .select('id, start_date, end_date')
      .in('team_id', chunk);
    if (bootcampsError) throw bootcampsError;
    bootcamps = bootcamps.concat(bootcampsData || []);
  }

  const { data: serverTimeData } = await supabase.rpc('get_server_time');
  const nowMs = new Date(serverTimeData || Date.now()).getTime();

  let bootcampsScheduled = 0;
  let bootcampsActive = 0;
  let bootcampsCompleted = 0;
  bootcamps.forEach((bc) => {
    const startTs = new Date(bc.start_date).getTime();
    const endTs = new Date(bc.end_date).getTime();
    if (startTs > nowMs) bootcampsScheduled += 1;
    else if (endTs <= nowMs) bootcampsCompleted += 1;
    else bootcampsActive += 1;
  });

  const bootcampIds = bootcamps.map((b) => b.id);
  let checkpointsCompleted = 0;
  let reflectionsWritten = 0;
  let activeUsers = 0;

  if (bootcampIds.length > 0) {
    const [checkpoints, reflections, completionUserIds] = await Promise.all([
      countInChunks('lesson_completions', 'bootcamp_id', bootcampIds),
      countInChunks('user_reflections', 'bootcamp_id', bootcampIds),
      fetchDistinctCompletionUserIds(bootcampIds),
    ]);
    checkpointsCompleted = checkpoints;
    reflectionsWritten = reflections;
    activeUsers = completionUserIds.size;
  }

  return {
    users: userIds.size,
    activeUsers,
    teams: teamIds.length,
    checkpointsCompleted,
    bootcamps: bootcamps.length,
    bootcampsScheduled,
    bootcampsActive,
    bootcampsCompleted,
    reflectionsWritten,
  };
}
